import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Trans } from 'react-i18next'
import {
  UserFeed,
  isFeedItemMedia,
  isImageFeedContent,
} from 'shared/src/api/types'
import { Link } from 'react-router-dom'
import { addLocation } from 'shared/src/utils/addNavigate'
import cx from 'classnames'

import { SocialButtons } from 'components/feed/profile/SocialButtons'
import { CustomLink } from 'components/explore/CustomLink'
import { serifFontFamily } from 'utils/styles'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeFluidCreatorUrl } from 'utils/routes'
import { addRegisterPage } from 'components/register'
import { MediaProductStack } from './MediaProductStack'

const useStyles = createUseStyles({
  container: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  customLinkContainer: {
    marginTop: 25,
  },
  myRodeoContainer: {
    display: 'flex',
    height: 163,
    marginLeft: 24,
    marginRight: 24,
    marginBottom: 12,
    borderRadius: 12,
    backgroundColor: '#050505',
    justifyContent: 'space-between',
    textDecoration: 'none',
    overflow: 'hidden',
  },
  myRodeoTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    fontFamily: serifFontFamily,
    color: '#fff',
    fontWeight: 500,
    position: 'absolute',
  },
  myText: {
    fontSize: 18,
    lineHeight: '24px',
    position: 'absolute',
    left: 18,
    top: 31,
  },
  rodeoText: {
    fontSize: 75,
    lineHeight: '71px',
    letterSpacing: '-0.01em',
    position: 'absolute',
    left: 16,
    top: 40,
  },
  merchTitle: {
    fontSize: 60,
    lineHeight: '71px',
    letterSpacing: '-0.6px',
    position: 'absolute',
    left: 29,
    top: 29,
    zIndex: 1,
  },
  merchSubtitle: {
    fontSize: 20,
    lineHeight: '24px',
    letterSpacing: '-0.2px',
    position: 'absolute',
    left: 29,
    top: 92,
    zIndex: 1,
  },
  italics: {
    fontStyle: 'italic',
  },
  mediaProductStackContainer: {
    width: '100%',
    position: 'relative',
  },
  halfOpacity: {
    opacity: 0.5,
  },
  mediaStack0: {
    position: 'absolute',
    right: 3,
    top: 71,
    width: 101,
  },
  mediaStack1: {
    position: 'absolute',
    right: -3,
    bottom: 105,
    width: 98,
  },
  mediaStack2: {
    position: 'absolute',
    right: 107,
    bottom: 90,
    width: 81,
  },
  mediaStack3: {
    position: 'absolute',
    right: 128,
    top: 99,
    width: 102,
  },
})

interface MyRodeoLinkProps {
  feed: UserFeed
  onClick: () => void
}

export const MyRodeoLink: FC<MyRodeoLinkProps> = flowMax(
  addDisplayName('MyRodeoLink'),
  addLocation,
  addTranslationHelpers,
  addProps(({ feed: { media } }) => ({
    displayMedia: media
      .filter((m) => isFeedItemMedia(m) && isImageFeedContent(m.content))
      .slice(0, 4),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addProps(({ classes }) => ({
    mediaStackClasses: [
      classes.mediaStack0,
      classes.mediaStack1,
      classes.mediaStack2,
      classes.mediaStack3,
    ],
  })),
  ({
    feed: { user },
    displayMedia,
    location,
    onClick,
    mediaStackClasses,
    classes,
    t,
  }) => (
    <Link
      to={makeFluidCreatorUrl(user.username)}
      onClick={onClick}
      className={classes.myRodeoContainer}
      state={location.state}
      replace
    >
      <div className={cx(classes.myRodeoTextContainer)}>
        {user.showMerch ? (
          <>
            <div className={classes.merchTitle}>{t('profile.merch')}</div>
            <div className={classes.merchSubtitle}>
              <Trans t={t} i18nKey="profile.merchSubtitle">
                <span className={classes.italics} />
              </Trans>
            </div>
          </>
        ) : (
          <>
            <div className={classes.myText}>{t('profile.my')}</div>
            <div className={classes.rodeoText}>
              <Trans t={t} i18nKey="profile.rodeo">
                <span className={classes.italics} />
              </Trans>
            </div>
          </>
        )}
      </div>
      <div
        className={cx(
          classes.mediaProductStackContainer,
          user.showMerch && classes.halfOpacity
        )}
      >
        {displayMedia.map(
          (media, mediaIndex) =>
            isFeedItemMedia(media) && (
              <MediaProductStack
                media={media}
                className={mediaStackClasses[mediaIndex]}
              />
            )
        )}
      </div>
    </Link>
  )
)

interface Props {
  feed: UserFeed
  onClickMyRodeo: () => void
}

export const Links: FC<Props> = flowMax(
  addDisplayName('About'),
  addRegisterPage,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ feed, feed: { user }, onClickMyRodeo, classes }) => (
    <div className={classes.container}>
      <SocialButtons creator={user} />
      <div className={classes.customLinkContainer}>
        {!user.showMerch && (
          <MyRodeoLink feed={feed} onClick={onClickMyRodeo} />
        )}
        {user.customLinks.map((link) => (
          <CustomLink link={link} key={link.id} />
        ))}
      </div>
    </div>
  )
)
